import type { ComponentProps, FocusEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { ThemeProvider } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { ctw } from '@/common';
import { muiTheme } from '@/common/mui-theme';
import { Paper } from '@/components/atoms/Paper';

export type AutocompleteOption = {
  label?: string;
  value: string;
};

export type AutocompleteChangeEvent = React.ChangeEvent<{
  name?: string;
  value: string;
}>;

export type AutocompleteInputProps = {
  id?: string;
  value?: string;
  options: AutocompleteOption[];
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  testId?: string;
  textInputClassName?: string;
  onChange: (event: AutocompleteChangeEvent) => void;
  onBlur?: (event: FocusEvent<any>) => void;
  onFocus?: (event: FocusEvent<any>) => void;
};

export const AutocompleteInput = ({
  id,
  options,
  value = '',
  placeholder,
  name,
  disabled,
  testId,
  textInputClassName,
  onChange,
  onBlur,
  onFocus,
}: AutocompleteInputProps) => {
  const safeValue = useMemo(() => {
    if (typeof value !== 'string') {
      console.warn('AutocompleteInput: value is not a string', value);
      console.warn('Empty string will be used');

      return '';
    }

    return value;
  }, [value]);

  const selectedOption = useMemo(() => {
    return options.find(option => option.value === safeValue);
  }, [options, safeValue]);

  const displayValue = useMemo(() => {
    return selectedOption?.label || safeValue;
  }, [selectedOption, safeValue]);

  const handleChange: NonNullable<ComponentProps<typeof Autocomplete>['onChange']> = useCallback(
    (_, newValue) => {
      onChange({
        target: {
          value: newValue
            ? typeof newValue === 'string'
              ? newValue
              : (newValue as AutocompleteOption)?.value
            : undefined,
          name,
        },
      } as AutocompleteChangeEvent);
    },
    [name, onChange],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        target: {
          name,
          value: event.target.value,
        },
      } as AutocompleteChangeEvent);
    },
    [name, onChange],
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Autocomplete
        id={id}
        disablePortal
        options={options}
        getOptionLabel={(option: AutocompleteOption | string) =>
          typeof option === 'string' ? option : option.label || option.value
        }
        freeSolo
        inputValue={displayValue}
        value={safeValue ? selectedOption || safeValue : null}
        PaperComponent={Paper as ComponentProps<typeof Autocomplete>['PaperComponent']}
        onChange={handleChange}
        disabled={disabled}
        onFocus={onFocus}
        slotProps={{
          paper: {
            className: 'mt-2 mb-2 w-full',
          },
          clearIndicator: {
            disableRipple: true,
          },
        }}
        renderOption={(props, option: string | AutocompleteOption) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label || option.value;

          return (
            <li {...props} key={optionValue} data-testid={testId ? `${testId}-option` : undefined}>
              {optionLabel}
            </li>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="standard"
            fullWidth
            size="small"
            placeholder={placeholder}
            disabled={disabled}
            onBlur={onBlur}
            //@ts-nocheck
            InputProps={{
              ...params.InputProps,
              classes: {
                root: ctw(
                  'border-input bg-background placeholder:text-muted-foreground rounded-md border px-3 py-0 text-sm shadow-none transition-colors',
                  textInputClassName,
                ),
                focused: 'border-input ring-ring ring-1',
                disabled: 'opacity-50 cursor-not-allowed',
              },
              disableUnderline: true,
            }}
            //@ts-nocheck
            inputProps={{
              ...params.inputProps,
              className: 'py-0 px-0 h-9',
              'data-testid': testId,
            }}
            onChange={handleInputChange}
          />
        )}
      />
    </ThemeProvider>
  );
};
